import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Upload } from "antd";
import dayjs from "dayjs";
import { ImageIcon, X } from "lucide-react";
import { useDispatch } from "react-redux";
import AxiosInstance from "../../services/networkServices";
import { uploadImage, getUploadedMediaRecord } from "../../http/uploads";
import { setLoading, showToast } from "../../store/slices/common";
import { validateFileExtension } from "../../utils";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";
import CustomTextField from "../../components/FormElements/CustomTextField";
import AlternativeNamesFields from "../../components/FormElements/AlternativeNamesFields";
import {
  alternativeNameOverLimit,
  alternativeNamesPayload,
  emptyAlternativeNames,
} from "../../components/FormElements/alternativeLanguages";
import FeedGroupSizeField, {
  normalizeFeedGroupSize,
} from "../../components/FormElements/FeedGroupSizeField";
import {
  isFeedGroupSizeEligibleCategory,
  isStickerEligibleCategory,
  isValidMediaId,
  labelStyles,
  MAX_STICKERS,
} from "../ManageChallenge/helpers";

const { Dragger } = Upload;

const DATE_FORMAT = "MMM D, YYYY h:mm A";

/**
 * Window a new challenge gets from the theme. On a saved theme the server
 * applies the theme's real dates (a running theme's start is in the past on
 * purpose). On an unsaved theme the dates are sent by the client, and the
 * create rule rejects a past start, so it is moved to "now".
 */
export function challengeWindowFromTheme(
  themeStartDate,
  themeEndDate,
  { clampStart = true } = {}
) {
  if (!themeStartDate || !themeEndDate) return null;
  const themeStart = dayjs(themeStartDate);
  const themeEnd = dayjs(themeEndDate);
  if (!themeStart.isValid() || !themeEnd.isValid() || !themeEnd.isAfter(themeStart)) {
    return { error: "Set a theme end date that is after the start." };
  }
  if (!themeEnd.isAfter(dayjs())) {
    return {
      error: "This theme has already ended, so a challenge can't use its dates.",
    };
  }
  const startIsPast = !themeStart.isAfter(dayjs());
  const start = clampStart && startIsPast ? dayjs().add(1, "minute") : themeStart;
  if (!themeEnd.isAfter(start)) {
    return { error: "The theme ends too soon to schedule a challenge." };
  }
  return { start, end: themeEnd, startAdjusted: clampStart && startIsPast };
}

function librarySelection(libraryStickers) {
  return (libraryStickers || [])
    .filter(
      (sticker) => isValidMediaId(String(sticker?.mediaId || "")) && sticker?.url
    )
    .slice(0, MAX_STICKERS)
    .map((sticker) => ({
      mediaId: String(sticker.mediaId),
      url: sticker.url,
    }));
}

/**
 * With `themeId` (saved theme) the server derives the dates, applies the
 * selected stickers, and links the challenge in the same request. Without it
 * (theme not saved yet) the dates are sent explicitly and the parent links the
 * challenge when the theme is saved.
 */
const ThemeChallengeDialog = ({
  open,
  onClose,
  themeId,
  themeStartDate,
  themeEndDate,
  libraryStickers,
  onCreated,
}) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState("");
  const [alternativeNames, setAlternativeNames] = useState(emptyAlternativeNames);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [feedGroupSize, setFeedGroupSize] = useState(null);
  const [background, setBackground] = useState(null);
  const [stickers, setStickers] = useState([]);
  const [saving, setSaving] = useState(false);
  const stickersRef = useRef([]);
  syncStickerListRef(stickersRef, stickers);
  const stickerUploadRef = useRef(null);
  if (!stickerUploadRef.current) {
    stickerUploadRef.current = createStickerUploadHandler({
      max: MAX_STICKERS,
      getCurrent: () => stickersRef.current.length,
      folder: "challenge-stickers",
      metadata: () => ({ type: "sticker" }),
      onUploaded: (media) => {
        setStickers(rememberUploadedSticker(stickersRef, media, MAX_STICKERS));
      },
      dispatch,
      setLoading: (value) => dispatch(setLoading(value)),
    });
  }

  const selectedCategory = categories.find((category) => category._id === categoryId);
  const stickersAllowed = isStickerEligibleCategory(selectedCategory);
  const feedGroupAllowed = isFeedGroupSizeEligibleCategory(selectedCategory);
  const windowInfo = challengeWindowFromTheme(themeStartDate, themeEndDate, {
    clampStart: !themeId,
  });

  useEffect(() => {
    if (!open) return undefined;
    setTitle("");
    setAlternativeNames(emptyAlternativeNames());
    setCategoryId("");
    setFeedGroupSize(null);
    setBackground(null);
    setStickers(librarySelection(libraryStickers));
    return undefined;
    // Reset only when the dialog opens. libraryStickers is read at that moment.
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await AxiosInstance.post("/category/list", {
          page: 1,
          limit: 100,
        });
        if (!cancelled && response.data?.status) {
          setCategories(response.data.data?.list || []);
        }
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleBackgroundUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const validFormats = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    const isValidMimeType = file.type && validFormats.includes(file.type);
    const isValidExtension = validateFileExtension(file.name, "");
    if (!isValidMimeType && !isValidExtension) {
      dispatch(
        showToast({
          color: "error",
          msg: "Invalid file format. Please upload a JPG, JPEG, or PNG image.",
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatch(
        showToast({
          color: "error",
          msg: "File size exceeds 5MB limit. Please upload a smaller file.",
        })
      );
      onError(new Error("File size too large"));
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "challenges");
      const res = await uploadImage(formData);
      const media = getUploadedMediaRecord(res);
      if (!media?._id || !media?.url) {
        throw new Error("Upload response did not include a media record");
      }
      setBackground(media);
      onSuccess(media);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to upload background image. Please try again.",
        })
      );
      onError(error);
    }
  };

  const removeSticker = (mediaId) => {
    const next = (stickersRef.current || []).filter(
      (sticker) => sticker.mediaId !== mediaId
    );
    stickersRef.current = next;
    setStickers(next);
  };

  const addBackSticker = (sticker) => {
    const prev = stickersRef.current || [];
    if (prev.some((item) => item.mediaId === String(sticker.mediaId))) return;
    if (prev.length >= MAX_STICKERS) return;
    const next = [
      ...prev,
      { mediaId: String(sticker.mediaId), url: sticker.url },
    ];
    stickersRef.current = next;
    setStickers(next);
  };

  const handleCreate = async () => {
    const trimmed = title.trim();
    if (trimmed.length < 3) {
      dispatch(
        showToast({
          color: "error",
          msg: "Challenge name must be at least 3 characters.",
        })
      );
      return;
    }
    if (trimmed.length > 50) {
      dispatch(
        showToast({
          color: "error",
          msg: "Challenge name must not exceed 50 characters.",
        })
      );
      return;
    }
    const longAlternative = alternativeNameOverLimit(alternativeNames, 50);
    if (longAlternative) {
      dispatch(
        showToast({
          color: "error",
          msg: `${longAlternative.label} name must not exceed 50 characters.`,
        })
      );
      return;
    }
    if (!categoryId) {
      dispatch(showToast({ color: "error", msg: "Category is required." }));
      return;
    }
    if (!windowInfo || windowInfo.error) {
      dispatch(
        showToast({
          color: "error",
          msg: windowInfo?.error || "Set the theme start and end first.",
        })
      );
      return;
    }

    const payload = {
      title: trimmed,
      localizedTitles: alternativeNamesPayload(alternativeNames),
      categoryId,
    };
    if (themeId) {
      payload.themeId = themeId;
    } else {
      payload.startDate = windowInfo.start.toISOString();
      payload.endDate = windowInfo.end.toISOString();
    }
    if (isValidMediaId(background?._id)) {
      payload.backgroundImageId = background._id;
    }
    if (stickersAllowed) {
      // Always explicit: an empty list means "none", not "inherit the theme's".
      payload.stickerIds = stickers
        .map((sticker) => sticker.mediaId)
        .filter(isValidMediaId);
    }
    if (feedGroupAllowed) {
      payload.feedGroupSize = normalizeFeedGroupSize(feedGroupSize);
    }

    try {
      setSaving(true);
      const response = await AxiosInstance.post("/category/challenge", payload);
      const created = response?.data?.data;
      if (!created?._id) {
        throw new Error("Challenge was created but no id was returned.");
      }
      onCreated({
        _id: created._id,
        title: created.title || trimmed,
        themeId: created.themeId || null,
      });
      onClose();
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            error.message ||
            "Failed to create challenge. Please try again.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedIds = new Set(stickers.map((sticker) => sticker.mediaId));
  const addBack = (libraryStickers || []).filter(
    (sticker) =>
      isValidMediaId(String(sticker?.mediaId || "")) &&
      sticker?.url &&
      !selectedIds.has(String(sticker.mediaId))
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      sx={{
        zIndex: 1300,
        "& .MuiDialog-container .MuiPaper-elevation": {
          borderRadius: "8px",
          width: "480px",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 700, pb: 1 }}>
        Add challenge to theme
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: "6px",
            backgroundColor: "#f4f6fb",
          }}
        >
          {windowInfo?.error ? (
            <Typography variant="body2" color="error">
              {windowInfo.error}
            </Typography>
          ) : windowInfo ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Uses theme dates: {windowInfo.start.format(DATE_FORMAT)} –{" "}
                {windowInfo.end.format(DATE_FORMAT)}
              </Typography>
              {windowInfo.startAdjusted && (
                <Typography variant="caption" color="text.secondary">
                  The theme has already started, so this challenge starts now
                  and ends with the theme.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="error">
              Set the theme start and end first.
            </Typography>
          )}
        </Box>

        <CustomTextField
          label="Title of the challenge"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 50 }}
        />
        <AlternativeNamesFields
          key={open ? "open" : "closed"}
          value={alternativeNames}
          onChange={setAlternativeNames}
          maxLength={50}
          onEnglishTitleChange={setTitle}
        />

        <FormControl sx={{ marginTop: "16px" }} fullWidth>
          <InputLabel id="theme-challenge-category">Choose Category</InputLabel>
          <Select
            labelId="theme-challenge-category"
            value={categoryId}
            label="Choose Category"
            onChange={(event) => setCategoryId(event.target.value)}
            sx={{
              height: "55px",
              "& .MuiSelect-select": { color: "black" },
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category._id} value={category._id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={2} mb={1}>
          <label style={{ ...labelStyles, top: "-9px" }}>Background Image</label>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Optional. Landscape image, at least 1080×720.
          </Typography>
          {background?.url ? (
            <Box className="contest-upload-preview contest-upload-preview--wide">
              <img src={background.url} alt="background" />
              <button
                className="contest-upload-remove"
                onClick={() => setBackground(null)}
                type="button"
              >
                <X size={14} />
              </button>
            </Box>
          ) : (
            <Dragger
              showUploadList={false}
              customRequest={handleBackgroundUpload}
              accept="image/*"
              className="contest-upload-dragger"
            >
              <Box className="contest-upload-dragger-content">
                <ImageIcon size={28} strokeWidth={1.5} color="#667eea" />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Drag & drop background image here
                </Typography>
              </Box>
            </Dragger>
          )}
        </Box>

        {stickersAllowed && (
          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>
              Stickers (up to {MAX_STICKERS})
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Theme stickers are selected. Remove any that should not be on
              this challenge.
            </Typography>
            {stickers.length > 0 && (
              <Box className="sticker-preview-row" sx={{ mb: 1 }}>
                {stickers.map((sticker) => (
                  <Box
                    key={sticker.mediaId}
                    className="contest-upload-preview contest-upload-preview--sticker"
                  >
                    <img src={sticker.url} alt="sticker" />
                    <button
                      className="contest-upload-remove"
                      onClick={() => removeSticker(sticker.mediaId)}
                      type="button"
                    >
                      <X size={12} />
                    </button>
                  </Box>
                ))}
              </Box>
            )}
            {addBack.length > 0 && stickers.length < MAX_STICKERS && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Add back from the theme
                </Typography>
                <Box className="sticker-preview-row" sx={{ mt: 0.5 }}>
                  {addBack.map((sticker) => (
                    <Box
                      key={String(sticker.mediaId)}
                      component="button"
                      type="button"
                      onClick={() => addBackSticker(sticker)}
                      className="contest-upload-preview contest-upload-preview--sticker"
                      sx={{
                        border: "1px dashed #c5c5c5",
                        cursor: "pointer",
                        p: 0,
                        opacity: 0.7,
                      }}
                    >
                      <img src={sticker.url} alt="theme sticker" />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {stickers.length < MAX_STICKERS && (
              <Dragger
                multiple
                showUploadList={false}
                customRequest={stickerUploadRef.current}
                accept="image/*"
                className="contest-upload-dragger"
              >
                <Box className="contest-upload-dragger-content">
                  <ImageIcon size={28} strokeWidth={1.5} color="#667eea" />
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Drag & drop stickers here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to browse ({stickers.length} of {MAX_STICKERS})
                  </Typography>
                </Box>
              </Dragger>
            )}
          </Box>
        )}

        {feedGroupAllowed && (
          <FeedGroupSizeField
            value={feedGroupSize}
            onChange={setFeedGroupSize}
            defaultLabel="Default (group by category)"
            hint="How many pagents of this challenge appear back-to-back on the voting feed."
          />
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              border: "1px solid #888",
              color: "#888",
              borderRadius: "6px",
              fontSize: "14px",
              marginRight: "15px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || Boolean(windowInfo?.error) || !windowInfo}
            onClick={handleCreate}
            sx={{
              backgroundColor: "#142C5C",
              textTransform: "none",
              "&:hover": { backgroundColor: "#0f2247" },
            }}
          >
            {saving ? "Creating..." : "Add Challenge"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ThemeChallengeDialog;
